import { FC, useMemo } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useFormik } from 'formik'
import theme from 'theme'
import { ApplicationPermissionScope } from 'models'
import { ThemeButton, disabledStyles } from 'ui/components/buttons'
import LabeledInputV2 from 'ui/components/form-components-v2/labeled-input-v2'
import { PermissionTooltip } from 'ui/components/tooltip'
import {
  AMBIGUOUS_SITES_TITLE,
  applicationFormDefaults,
  applicationFormSchema,
  ApplicationFormValues,
  AUTH_METHOD_TITLE,
  INSUFFICIENT_PERMISSIONS,
  REQUESTED_PERMISSIONS_TITLE,
  TRY_AGAIN_LABEL
} from '../applicationSetup.constants'
import { ApplicationProviderCopy } from '../applicationProviders'
import { ConnectStepWrapper, primaryActionSx } from '../applicationSetup.styles'

export interface RetryPrompt {
  title: string
  message: string
  sites: string[]
  onRetry: () => void
}

interface Props {
  copy: ApplicationProviderCopy
  permissionScopes: ApplicationPermissionScope[]
  takenNames: string[]
  busy: boolean
  pageError: string
  noEditPermission: boolean
  onConnect: (values: ApplicationFormValues) => void
  /* Set once an instance exists whose approval did not land. The form gives way to the reason and
   * Try again, because that instance already carries its name. */
  retry?: RetryPrompt
}

interface ActionProps {
  label: string
  disabled: boolean
  busy: boolean
  noEditPermission: boolean
  type: 'submit' | 'button'
  onClick?: () => void
}

const PrimaryAction: FC<ActionProps> = ({ label, disabled, busy, noEditPermission, type, onClick }) =>
  noEditPermission ? (
    <PermissionTooltip
      PopperProps={{ sx: { marginTop: `-${theme.spacing.sm}px !important` } }}
      title={
        <Box className="tooltip-wrapper-permissions">
          <Typography className="tooltip-text">{INSUFFICIENT_PERMISSIONS}</Typography>
        </Box>
      }
    >
      <ThemeButton themevariant="primary" type="button" sx={{ ...primaryActionSx, ...disabledStyles.secondary }}>
        {label}
      </ThemeButton>
    </PermissionTooltip>
  ) : (
    <ThemeButton
      themevariant="primary"
      type={type}
      onClick={onClick}
      disabled={disabled}
      startIcon={busy && <CircularProgress size="24px" />}
      sx={primaryActionSx}
    >
      {label}
    </ThemeButton>
  )

/* Step one: what the connection is, whom to sign in as, what the consent screen will ask for, and
 * either the name for a new instance or the reason an existing one has no approval yet. */
const ConnectStep: FC<Props> = ({
  copy,
  permissionScopes,
  takenNames,
  busy,
  pageError,
  noEditPermission,
  onConnect,
  retry
}) => {
  const validationSchema = useMemo(() => applicationFormSchema(takenNames), [takenNames])

  const formik = useFormik<ApplicationFormValues>({
    initialValues: applicationFormDefaults,
    validationSchema,
    onSubmit: onConnect
  })

  const connectDisabled = busy || !formik.isValid || !formik.values.name

  return (
    <ConnectStepWrapper>
      <Box className="auth-method">
        <Typography className="block-title">{AUTH_METHOD_TITLE}</Typography>
        <Typography className="auth-method-value">{copy.authMethodLabel}</Typography>
        <Typography className="block-text">{copy.authMethodReassurance}</Typography>
      </Box>

      <Box className="service-account">
        <Typography className="block-title">{copy.serviceAccountTitle}</Typography>
        <Typography className="block-text">{copy.serviceAccountBody}</Typography>
      </Box>

      <Box className="permissions">
        <Typography className="block-title">{REQUESTED_PERMISSIONS_TITLE}</Typography>

        {permissionScopes.map((scope) => (
          <Box className="permission-item" key={scope.code}>
            <Box className="permission-bullet" />
            <Typography className="block-text">{scope.label}</Typography>
          </Box>
        ))}
      </Box>

      {retry ? (
        <Box className="retry-block">
          <Box className="retry-reason">
            <Typography className="block-title">{retry.title}</Typography>
            <Typography className="block-text">{retry.message}</Typography>

            {!!retry.sites.length && (
              <>
                <Typography className="block-text">{AMBIGUOUS_SITES_TITLE}</Typography>
                {retry.sites.map((site) => (
                  <Box className="permission-item" key={site}>
                    <Box className="permission-bullet" />
                    <Typography className="block-text">{site}</Typography>
                  </Box>
                ))}
              </>
            )}
          </Box>

          {!!pageError && <Typography className="page-error">{pageError}</Typography>}

          <PrimaryAction
            label={TRY_AGAIN_LABEL}
            disabled={busy}
            busy={busy}
            noEditPermission={noEditPermission}
            type="button"
            onClick={retry.onRetry}
          />
        </Box>
      ) : (
        <form autoComplete="off" onSubmit={formik.handleSubmit} className="connect-form">
          <LabeledInputV2
            name="name"
            label="Name"
            value={formik.values.name}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={busy || noEditPermission}
            helperText={formik.errors.name}
          />

          <LabeledInputV2
            name="description"
            label="Description (Optional)"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={busy || noEditPermission}
          />

          {!!pageError && <Typography className="page-error">{pageError}</Typography>}

          <PrimaryAction
            label={copy.connectButtonLabel}
            disabled={connectDisabled}
            busy={busy}
            noEditPermission={noEditPermission}
            type="submit"
          />
        </form>
      )}
    </ConnectStepWrapper>
  )
}

export default ConnectStep
